// Login page — visually mirrors ServiceAI's two-pane login per §1.3
// (binding). Provider buttons in ServiceAI order: Microsoft, then Google.
// "Email me a sign-in code" sits below the SSO row, matching ServiceAI's
// `btn-link-login` placement.
//
// Password + email-token flows are fully functional. The M365 button links to
// the OIDC challenge endpoint wired in Phase 5c (auto-link by verified email);
// callback failures redirect back here as `?error=…` and surface as a toast.
// The Google button still links to a challenge endpoint that is not yet wired.

import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import type {
  AuthResponse,
  EmailTokenRequest,
  EmailTokenVerifyRequest,
  PasswordLoginRequest,
  SelectTenantRequest,
} from '@shared/api-types';
import { ApiError, apiFetch } from '../../lib/apiClient';
import { useAuth } from '../../lib/auth';
import { capturedReturnTo } from '../../lib/capturedPage';
import { ToastHost } from '../../components/ToastHost';
import './Login.css';

type Step = 'email' | 'password' | 'token' | 'tenant-select';

// Local developer sign-in (AAI-115). `import.meta.env.DEV` is a build-time
// constant, so production bundles drop the panel and its requests entirely; in a
// dev build it still renders nothing unless the local API offers the feature.
const DevSignInPanel = import.meta.env.DEV ? lazy(() => import('./DevSignInPanel')) : null;

// Maps the `?error=` codes the backend's OAuth callbacks redirect with
// (see AuthController.M365Callback / GoogleCallback) to user-facing copy.
// AuthResponse.reason when the organization has turned off password sign-in
// (which also turns off emailed one-time codes) — mirrors LoginPolicy.SsoRequiredReason.
const SSO_REQUIRED_REASON = 'sso_required';

// Shown instead of "invalid credentials" for that case: the details may well be
// right, they're just not accepted here any more, and the fix is to use SSO. The
// wording says "below" because the refusal always lands the user back on the
// email step, which is the only step that renders the provider buttons.
const SSO_REQUIRED_MESSAGE =
  'Your organization requires signing in with Microsoft 365 or Google. Use one of the buttons below.';

const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  m365_not_provisioned:
    'No CloudRadial account is linked to that Microsoft email. Contact your platform admin.',
  m365_failed: "Microsoft sign-in didn't complete. Please try again.",
  m365_no_email:
    "Microsoft didn't return an email for your account. Try another sign-in method.",
  m365_no_tenant:
    "Your account isn't a member of any organization. Contact your platform admin.",
  google_not_provisioned:
    'No CloudRadial account is linked to that Google email. Contact your platform admin.',
  google_failed: "Google sign-in didn't complete. Please try again.",
  google_no_email:
    "Google didn't return an email for your account. Try another sign-in method.",
  google_email_unverified:
    "Your Google email isn't verified. Verify it with Google, or use another sign-in method.",
  google_no_tenant:
    "Your account isn't a member of any organization. Contact your platform admin.",
};

// `*_not_provisioned` callbacks also carry the `email` the IdP returned, so the
// message can name the exact account that was used — the common cause is a
// multi-account browser signing in with the wrong one, and the account chooser
// (prompt=select_account) lets the user re-pick.
const NOT_PROVISIONED_WITH_EMAIL: Record<string, (email: string) => string> = {
  m365_not_provisioned: (email) =>
    `No CloudRadial account is linked to ${email}. That's the Microsoft account you signed in with — if it's the wrong one, sign in again and pick another. Otherwise contact your platform admin.`,
  google_not_provisioned: (email) =>
    `No CloudRadial account is linked to ${email}. That's the Google account you signed in with — if it's the wrong one, sign in again and pick another. Otherwise contact your platform admin.`,
};

// A refused login returns 401 carrying the AuthResponse, so the reason is in
// ApiError.body rather than a parsed response.
function ssoRequired(body: unknown): boolean {
  return typeof body === 'object'
    && body !== null
    && (body as { reason?: unknown }).reason === SSO_REQUIRED_REASON;
}

// OAuth callback failures arrive as `/login?error=<code>`. Read it from the URL
// once so the message can seed the initial toast state (rather than calling
// setState inside an effect, which react-hooks/set-state-in-effect forbids).
function readOAuthCallbackError(): string | null {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('error');
  if (!code) {
    return null;
  }
  const email = params.get('email');
  if (email && NOT_PROVISIONED_WITH_EMAIL[code]) {
    return NOT_PROVISIONED_WITH_EMAIL[code](email);
  }
  return OAUTH_ERROR_MESSAGES[code] ?? 'Sign-in failed. Please try again.';
}

// A multi-org SSO sign-in bounces the browser to `/login?select=org` once the
// IdP has verified the user (see AuthController.{M365,Google}Callback). The
// login response with its tenant list was never in this page's JS, so we
// re-fetch the candidates from the short-lived tenant-select cookie.
function readSelectOrg(): boolean {
  return new URLSearchParams(window.location.search).get('select') === 'org';
}

interface MaybeTenantPickerProps {
  response: AuthResponse;
  onPick: (tenantId: string) => void;
  busy: boolean;
}

function TenantPicker({ response, onPick, busy }: MaybeTenantPickerProps) {
  return (
    <div className="step">
      <h1 className="step-title">Choose an organization</h1>
      <p className="step-sub">You belong to more than one tenant. Pick one to continue.</p>
      {(response.tenants ?? []).map((tenant) => (
        <button
          key={tenant.id}
          type="button"
          className="btn-sso"
          onClick={() => onPick(tenant.id)}
          disabled={busy}
        >
          <span>{tenant.name}</span>
        </button>
      ))}
    </div>
  );
}

const MICROSOFT_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
    <rect x="2" y="2" width="9" height="9" fill="#F25022" />
    <rect x="13" y="2" width="9" height="9" fill="#7FBA00" />
    <rect x="2" y="13" width="9" height="9" fill="#00A4EF" />
    <rect x="13" y="13" width="9" height="9" fill="#FFB900" />
  </svg>
);

const GOOGLE_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09Z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.43.34-2.1V7.07H2.18A11 11 0 0 0 1 12c0 1.78.43 3.46 1.18 4.93l3.66-2.83Z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38Z"
    />
  </svg>
);

const MAIL_ICON = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 7 9 6 9-6" />
  </svg>
);

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, refresh } = useAuth();

  // Where this sign-in is headed, shared by all three exits below: the
  // password / one-time-code flow, the organization chooser, and the bounce for
  // a visit that is already signed in.
  const returnTo = capturedReturnTo(location.state);

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  // Seed the toast from any OAuth callback error in the URL (read once) so it
  // shows on first paint without a setState-in-effect.
  const [toast, setToast] = useState<string | null>(readOAuthCallbackError);
  const [toastStyle, setToastStyle] = useState<'info' | 'error'>(() =>
    readOAuthCallbackError() ? 'error' : 'info',
  );
  const [pendingResponse, setPendingResponse] = useState<AuthResponse | null>(null);
  // Capture the post-SSO `?select=org` signal at render time (read once), BEFORE
  // the param-strip effect below removes it from the URL — otherwise the strip
  // runs first on mount and the org-chooser fetch would read an already-cleaned
  // search string and never fire. Same read-once pattern as readOAuthCallbackError.
  const [wantsOrgSelect] = useState(readSelectOrg);

  const showToast = useCallback((message: string, style: 'info' | 'error' = 'info') => {
    setToastStyle(style);
    setToast(message);
  }, []);

  // The OAuth callback error (if any) is already surfaced via the initial toast
  // state above; strip the one-shot `error` / `select` params on mount so a
  // refresh doesn't replay them, preserving any other query params and the
  // hash. Side-effect only (no setState) — effect-safe.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('error') && !params.has('select') && !params.has('email')) {
      return;
    }
    params.delete('error');
    params.delete('select');
    // The provider email rides on `*_not_provisioned` redirects only so the
    // toast can name the account that was used — strip it here too so it doesn't
    // linger in the URL or browser history after it's been read once.
    params.delete('email');
    const query = params.toString();
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
    );
  }, []);

  // Post-SSO multi-org landing: `/login?select=org` means a verified user with
  // >1 tenant needs to choose one. Fetch the candidates from the tenant-select
  // cookie and show the picker. A 401 means the short-lived cookie expired —
  // restart sign-in. setState happens only after the await, so this is
  // effect-safe (no synchronous set-state-in-effect).
  useEffect(() => {
    if (!wantsOrgSelect) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const response = await apiFetch<AuthResponse>('/api/auth/tenant-options');
        if (cancelled) return;
        setPendingResponse(response);
        setStep('tenant-select');
      } catch {
        if (cancelled) return;
        showToast('Your sign-in session expired. Please sign in again.', 'error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [wantsOrgSelect, showToast]);

  const redirectAfterAuth = useCallback(
    async (response: AuthResponse) => {
      if (response.requiresTenantSelection) {
        setPendingResponse(response);
        setStep('tenant-select');
        return;
      }
      // Hydrate the auth context, then hand the user back the page they asked
      // for — the dashboard only when they asked for nothing in particular.
      await refresh();
      navigate(returnTo, { replace: true });
    },
    [navigate, refresh, returnTo],
  );

  // Finalize a multi-org sign-in: the verified user id lives in the
  // tenant-select cookie, so we send only the chosen tenant. On success the real
  // crauto-auth cookie is set — hydrate the context and navigate (the route
  // guard still routes an MFA-pending password session to /mfa-required). The
  // picker renders on /login without navigating, so the captured page is still
  // in the router state here and survives the detour through the chooser.
  const handlePickTenant = useCallback(
    async (tenantId: string) => {
      setLoading(true);
      try {
        const body: SelectTenantRequest = { tenantId };
        await apiFetch<AuthResponse>('/api/auth/select-tenant', { method: 'POST', body });
        await refresh();
        navigate(returnTo, { replace: true });
      } catch {
        showToast('Could not sign in to that organization. Please sign in again.', 'error');
      } finally {
        setLoading(false);
      }
    },
    [navigate, refresh, returnTo, showToast],
  );

  // A tenant that has turned off password + email-code sign-in refuses both
  // factors. The password and token steps render no provider buttons, so leaving
  // the user there would show a message pointing at controls that aren't on
  // screen. Return to the email step — which does render them — and drop the
  // entered secret on the way.
  const refuseWithSsoRequired = () => {
    setPassword('');
    setCode('');
    setStep('email');
    setFieldError(SSO_REQUIRED_MESSAGE);
  };

  const handleEmailContinue = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldError(null);
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setFieldError('Please enter a valid email address.');
      return;
    }
    setStep('password');
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldError(null);
    setLoading(true);
    try {
      const body: PasswordLoginRequest = { email, password };
      const response = await apiFetch<AuthResponse>('/api/auth/password/login', {
        method: 'POST',
        body,
      });
      if (!response.authenticated) {
        if (response.reason === SSO_REQUIRED_REASON) refuseWithSsoRequired();
        else setFieldError('Invalid email or password.');
        return;
      }
      await redirectAfterAuth(response);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        if (ssoRequired(err.body)) refuseWithSsoRequired();
        else setFieldError('Invalid email or password.');
      } else {
        showToast('Sign-in failed. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRequestEmailToken = async () => {
    setFieldError(null);
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setFieldError('Enter a valid email address first.');
      return;
    }
    setLoading(true);
    try {
      const body: EmailTokenRequest = { email };
      await apiFetch<void>('/api/auth/email-token/request', { method: 'POST', body });
      setStep('token');
      showToast('Sign-in code sent. Check your email.');
    } catch {
      showToast('Could not send sign-in code. Try again in a moment.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyToken = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldError(null);
    if (!/^\d{6}$/.test(code)) {
      setFieldError('Enter the 6-digit code from the email.');
      return;
    }
    setLoading(true);
    try {
      const body: EmailTokenVerifyRequest = { email, code };
      const response = await apiFetch<AuthResponse>('/api/auth/email-token/verify', {
        method: 'POST',
        body,
      });
      if (!response.authenticated) {
        if (response.reason === SSO_REQUIRED_REASON) refuseWithSsoRequired();
        else setFieldError('That code is invalid or expired.');
        return;
      }
      await redirectAfterAuth(response);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        if (ssoRequired(err.body)) refuseWithSsoRequired();
        else setFieldError('That code is invalid or expired.');
      } else {
        showToast('Verification failed. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const dismissToast = () => setToast(null);

  // Already signed in (e.g. opened /login with a live session): bounce into the
  // app instead of showing the form. This wins the race with redirectAfterAuth
  // once refresh() has hydrated the context, so it has to honour the captured
  // page too — otherwise a deep link would land differently depending on which
  // of the two got there first. Mirror RequireAuth's MFA gate so an MFA-pending
  // session lands on the challenge, not the shell — handing the captured page on
  // the way RequireAuth does, so the challenge returns there once it is passed.
  // While the /api/me probe is still in flight ('unknown') the form renders, so
  // anonymous sign-ins aren't blocked behind the probe.
  if (state.status === 'authenticated') {
    return state.user.mfaSatisfied
      ? <Navigate to={returnTo} replace />
      : <Navigate to="/mfa-required" replace state={location.state} />;
  }

  return (
    <div className="login-container">
      <div className="login-left">
        <header className="auth-head">
          <div className="brand-lockup">
            <img
              className="brand-mark"
              src="/icons/light/automationai-96.png"
              alt=""
              aria-hidden="true"
              width={40}
              height={40}
            />
            <div className="brand-name">AutomationAI</div>
          </div>
        </header>

        <main className="auth-main">
          <div className="step-frame">
            {step === 'email' && (
              <form className="step" onSubmit={handleEmailContinue}>
                <h1 className="step-title">Welcome</h1>
                <p className="step-sub">Sign in to CloudRadial AutomationAI</p>

                <label className="field">
                  <span className="field-label">Work email</span>
                  <div className={`field-input ${fieldError ? 'has-error' : ''}`}>
                    <span aria-hidden="true">{MAIL_ICON}</span>
                    <input
                      type="email"
                      autoComplete="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setFieldError(null);
                      }}
                    />
                  </div>
                  {fieldError && <div className="field-msg" role="alert">{fieldError}</div>}
                </label>

                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading || !email}
                  data-testid="email-continue-btn"
                >
                  Continue
                </button>

                <div className="divider"><span>or</span></div>

                <div className="sso-grid">
                  {/* Provider order mirrors ServiceAI: Microsoft, then Google. */}
                  <a
                    className="btn-sso"
                    href="/api/auth/m365/challenge"
                    aria-label="Sign in with Microsoft"
                  >
                    <span>{MICROSOFT_ICON}</span>
                    <span>Microsoft</span>
                  </a>
                  <a
                    className="btn-sso"
                    href="/api/auth/google/challenge"
                    aria-label="Sign in with Google"
                  >
                    <span>{GOOGLE_ICON}</span>
                    <span>Google</span>
                  </a>
                </div>

                <button
                  type="button"
                  className="btn-sso btn-link-login"
                  onClick={handleRequestEmailToken}
                  disabled={loading}
                  data-testid="email-token-btn"
                >
                  <span>{MAIL_ICON}</span>
                  <span>Email me a sign-in code</span>
                </button>
              </form>
            )}

            {step === 'email' && DevSignInPanel && (
              <Suspense fallback={null}>
                <DevSignInPanel />
              </Suspense>
            )}

            {step === 'password' && (
              <form className="step" onSubmit={handlePasswordSubmit}>
                <h1 className="step-title">Enter your password</h1>
                <p className="step-sub">{email}</p>

                <label className="field">
                  <span className="field-label">Password</span>
                  <div className={`field-input ${fieldError ? 'has-error' : ''}`}>
                    <input
                      type="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setFieldError(null);
                      }}
                      placeholder="••••••••"
                    />
                  </div>
                  {fieldError && <div className="field-msg" role="alert">{fieldError}</div>}
                </label>

                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading || !password}
                  data-testid="password-signin-btn"
                >
                  {loading ? 'Signing in…' : 'Sign in'}
                </button>

                <button
                  type="button"
                  className="btn-sso btn-link-login"
                  onClick={handleRequestEmailToken}
                  disabled={loading}
                >
                  Email me a sign-in code instead
                </button>
              </form>
            )}

            {step === 'token' && (
              <form className="step" onSubmit={handleVerifyToken}>
                <h1 className="step-title">Enter your code</h1>
                <p className="step-sub">We sent a 6-digit code to {email}</p>

                <div className={`code-row ${fieldError ? 'has-error' : ''}`}>
                  <input
                    className="code-digit-input"
                    style={{ gridColumn: 'span 6' }}
                    inputMode="numeric"
                    maxLength={6}
                    autoComplete="one-time-code"
                    value={code}
                    onChange={(e) => {
                      setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                      setFieldError(null);
                    }}
                    aria-label="Six-digit code"
                  />
                </div>
                {fieldError && <div className="field-msg" role="alert">{fieldError}</div>}

                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading || code.length !== 6}
                  data-testid="token-verify-btn"
                >
                  {loading ? 'Verifying…' : 'Verify and continue'}
                </button>

                <button
                  type="button"
                  className="btn-sso btn-link-login"
                  onClick={handleRequestEmailToken}
                  disabled={loading}
                >
                  Resend code
                </button>
              </form>
            )}

            {step === 'tenant-select' && pendingResponse && (
              <TenantPicker response={pendingResponse} onPick={handlePickTenant} busy={loading} />
            )}
          </div>
        </main>

        <footer className="auth-foot">
          <div>© {new Date().getFullYear()} CloudRadial</div>
          <div style={{ display: 'inline-flex', gap: 16 }}>
            <a target="_blank" rel="noreferrer" href="https://www.cloudradial.com/privacy">Privacy</a>
            <a target="_blank" rel="noreferrer" href="https://www.cloudradial.com/terms">Terms</a>
          </div>
        </footer>
      </div>

      <div className="login-right">
        <div className="orb-wrap" aria-hidden="true">
          <div className="orb orb-a" />
          <div className="orb orb-b" />
          <div className="orb orb-c" />
          <div className="orb-grid" />
        </div>
        {/* CloudRadial corporate logo — dark wordmark on the light panel, white
            wordmark on the dark panel (ServiceAI parity). */}
        <img
          src="/assets/CloudRadial-Dark-Logo-2.png"
          alt="CloudRadial"
          className="login-brand-logo login-brand-logo-light"
        />
        <img
          src="/assets/CloudRadial-White-Logo-2.png"
          alt="CloudRadial"
          className="login-brand-logo login-brand-logo-dark"
        />
      </div>

      <ToastHost toast={toast} kind={toastStyle} onClose={dismissToast} />
    </div>
  );
}

export default Login;
